import createClient, { type Middleware } from 'openapi-fetch';
import type { paths as generatedPaths, components } from './schema';
import type { AddendumPaths } from './addendum';
import type { MonitoringPaths } from './monitoring';
import type { IngestPaths } from './ingest';
import type { RecordingPaths } from './recording';
import type { SupportPaths } from './support';
import type { AIPaths } from './ai';
import type { ConnectPaths } from './connect';
import type { RecoPaths } from './reco';
import type { InternalChannelPaths } from './internalChannels';
import type { ViewerAccountPaths } from './viewerAccounts';
import type { ViewerPaths } from './viewers';
import type { PathwayPaths } from './pathways';
import type { EncoderPaths } from './encoders';
import type { VodLipsyncPaths } from './vodLipsync';
import type { AppCrashPaths } from './appCrashes';
import type { TenantAnalyticsPaths } from './tenantAnalytics';

/**
 * The CSRF header `X-VS-Request` is declared as a required parameter on every cookie-authenticated write in the
 * OpenAPI file; the client middleware sets it, so call sites must not have to. This makes it optional in the types.
 */
type OptionalCsrf<Op> = Op extends { parameters: { header: infer H } }
  ? Omit<Op, 'parameters'> & { parameters: Omit<Op['parameters'], 'header'> & { header?: Partial<H> } }
  : Op;
type RelaxPaths<P> = { [Path in keyof P]: { [Method in keyof P[Path]]: OptionalCsrf<P[Path][Method]> } };

/** Generated paths (CSRF header relaxed) + the hand-typed addendum routes (deleted block by block as pins advance). */
export type paths = RelaxPaths<generatedPaths> & AddendumPaths & MonitoringPaths & IngestPaths & RecordingPaths & SupportPaths & AIPaths & ConnectPaths & RecoPaths & InternalChannelPaths & ViewerAccountPaths & ViewerPaths & PathwayPaths & EncoderPaths & VodLipsyncPaths & AppCrashPaths & TenantAnalyticsPaths;
import { toApiError } from './errors';

export type { components };
export type Schemas = components['schemas'];
export type Me = Schemas['Me'];
export type TenantSummary = Schemas['TenantSummary'];
export type Tenant = Schemas['Tenant'];
export type User = Schemas['User'];
export type Scope = Schemas['Scope'];
export type AssetSummary = Schemas['AssetSummary'];
export type AssetPage = Schemas['AssetPage'];
export type SearchMatch = Schemas['SearchMatch'];
export type Channel = Schemas['Channel'];
export type Programme = Schemas['Programme'];
export type Clip = Schemas['Clip'];
/** insight-v2 2.3: a prepared raw statistics export (GET /v1/stats/exports) */
export type StatsExport = Schemas['StatsExport'];
export type StatsExportReport = StatsExport['report'];
export type StatsExportFormat = StatsExport['format'];
/** kept for the dashboard tiles (was hand-typed before the M3 pin) */
export type ChannelSummary = Channel;
export { ApiError, type Problem } from './errors';
export * from './keys';
export * from './pagination';
export * from './addendum';
export * from './monitoring';
export * from './ingest';
export * from './recording';
export * from './pathways';
export * from './encoders';
export * from './vodLipsync';
export * from './appCrashes';
export * from './tenantAnalytics';
export * from './support';
export * from './ai';
export * from './connect';
export * from './internalChannels';
export * from './viewerAccounts';
export * from './viewers';
export * from './reco';

export interface ClientOptions {
  /** '' in the browser (same origin via the dev proxy / api. hostname), or an absolute URL */
  baseUrl?: string;
  /** called on 401 so the app can route to the login screen with a return URL */
  onUnauthorized?: (req: Request) => void;
  fetch?: typeof fetch;
}

/**
 * The only way the consoles talk to the control plane (foundation spec "Generated API client").
 * - credentials: 'include' — the vs_session cookie is the credential (addendum A1)
 * - X-VS-Request: 1 on every non-GET — CSRF (addendum A1)
 * - problem+json → ApiError; 401 → onUnauthorized; 429 → retryAfter
 */
export function createApi(opts: ClientOptions = {}) {
  const client = createClient<paths>({
    baseUrl: opts.baseUrl ?? '',
    credentials: 'include',
    fetch: opts.fetch,
  });

  const csrf: Middleware = {
    async onRequest({ request }) {
      if (request.method !== 'GET' && request.method !== 'HEAD') {
        request.headers.set('X-VS-Request', '1');
      }
      request.headers.set('Accept', 'application/json, application/problem+json');
      return request;
    },
    async onResponse({ request, response }) {
      if (response.ok) return response;
      if (response.status === 401) opts.onUnauthorized?.(request);
      throw await toApiError(response);
    },
  };
  client.use(csrf);
  return client;
}

export type Api = ReturnType<typeof createApi>;
